/**
 * Composable pour la gestion du formulaire de paiement
 * État, validation, et actions
 */

import { isValidEmail } from "@donoor/validators";

export const usePaymentForm = () => {
  const { t } = useI18n();

  // État du formulaire
  const form = ref({
    amount: "10", // Montant initial (sera remplacé par le montant par défaut configuré)
    donorName: "",
    donorEmail: "",
    message: "",
    isAnonymous: false,
  });

  // Montants suggérés
  const suggestedAmounts = ref([10, 20, 50]);

  // Type de don
  const donationType = ref<"one-time" | "subscription">("one-time");
  const subscriptionPeriod = ref<"weekly" | "monthly" | "yearly">("monthly");
  const subscriptionPeriods = ref([
    { value: "weekly" as const, label: t("don.periodesLabel.hebdomadaire"), discount: 0 },
    { value: "monthly" as const, label: t("don.periodesLabel.mensuel"), discount: 0 },
    { value: "yearly" as const, label: t("don.periodesLabel.annuel"), discount: 0 },
  ]);

  // Options
  const includeFees = ref(true); // Pré-sélectionné par défaut
  const showMessage = ref(false);

  // États UI
  const isLoading = ref(false);
  const showDetails = ref(false);

  // Validation
  const touched = reactive({
    email: false,
    name: false,
    amount: false,
  });

  const errors = reactive({
    email: "" as string | null,
    name: "" as string | null,
    amount: "" as string | null,
  });

  /**
   * Valider l'email
   */
  const validateEmail = () => {
    touched.email = true;

    if (!form.value.donorEmail.trim()) {
      errors.email = t("don.validationForm.emailRequis");
    } else if (!isValidEmail(form.value.donorEmail)) {
      errors.email = t("don.validationForm.emailInvalide");
    } else {
      errors.email = null;
    }
  };

  /**
   * Valider le nom
   */
  const validateName = () => {
    touched.name = true;

    if (!form.value.isAnonymous && !form.value.donorName.trim()) {
      errors.name = t("don.validationForm.nomRequis");
    } else {
      errors.name = null;
    }
  };

  /**
   * Valider le montant
   */
  const validateAmount = () => {
    touched.amount = true;
    const amount = parseFloat(form.value.amount);

    if (!form.value.amount || isNaN(amount)) {
      errors.amount = t("don.validationForm.montantRequis");
    } else if (amount <= 0) {
      errors.amount = t("don.validationForm.montantPositif");
    } else if (amount < 1) {
      errors.amount = t("don.validationForm.montantMinimum");
    } else {
      errors.amount = null;
    }
  };

  /**
   * Valider tous les champs
   */
  const validateAll = () => {
    validateEmail();
    validateName();
    validateAmount();
  };

  // `isValidEmail` réexporté depuis @donoor/validators — pas de redéfinition.

  // Reset de l'erreur `name` quand le donateur bascule en don anonyme
  // (le nom n'est plus requis). Symétrique : si on décoche anonyme et que le
  // champ nom était déjà touché ET vide, on re-valide pour ré-afficher l'erreur.
  watch(
    () => form.value.isAnonymous,
    (anonymous) => {
      if (anonymous) {
        errors.name = null;
        touched.name = false;
      } else if (touched.name) {
        validateName();
      }
    },
  );

  /**
   * Computed: Le formulaire est-il valide ?
   */
  const isValidForm = computed(() => {
    const hasAmount = parseFloat(form.value.amount) > 0;

    const hasValidEmail =
      form.value.donorEmail.trim() !== "" &&
      isValidEmail(form.value.donorEmail);

    // Si don anonyme, on n'a besoin que de l'email
    if (form.value.isAnonymous) {
      return hasAmount && hasValidEmail;
    }

    // Sinon, nom + email requis
    return hasAmount && form.value.donorName.trim() !== "" && hasValidEmail;
  });

  /**
   * Computed: Est-ce un abonnement ?
   */
  const isSubscription = computed(() => donationType.value === "subscription");

  /**
   * Reset du formulaire
   */
  const resetForm = () => {
    form.value = {
      amount: "20",
      donorName: "",
      donorEmail: "",
      message: "",
      isAnonymous: false,
    };
    includeFees.value = true;
    showMessage.value = false;
    isLoading.value = false;
    touched.email = false;
    touched.name = false;
    touched.amount = false;
    errors.email = null;
    errors.name = null;
    errors.amount = null;
  };

  return {
    // État
    form,
    suggestedAmounts,
    donationType,
    subscriptionPeriod,
    subscriptionPeriods,
    includeFees,
    showMessage,
    isLoading,
    showDetails,

    // Validation
    touched,
    errors,
    validateEmail,
    validateName,
    validateAmount,
    validateAll,
    isValidEmail,
    isValidForm,

    // Computed
    isSubscription,

    // Actions
    resetForm,
  };
};
